import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import localProducts from '../data/products';
import { getProducts, getSiteSettings } from '../lib/sanity';
import { updateSearchIndex } from '../utils/searchIndex';

const ProductContext = createContext();

export const useProducts = () => useContext(ProductContext);

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState(localProducts);
  const [siteSettings, setSiteSettings] = useState({
    announcementBar: 'Free shipping on US sticker & photocard orders! • Handcrafted with love in Hawaii',
    shopIsOpen: true,
    processingTime: '3–7 business days',
    aboutText: 'Star.Ryhwa is an indie creative studio based in Hawaii specializing in handcrafted K-Pop collages, customized accessories, and fandom goodies.',
  });
  const [loading, setLoading] = useState(true);

  const fetchCatalog = useCallback(async () => {
    try {
      // 1. Fetch Sanity products & settings
      const [fetchedSanityProducts, fetchedSettings] = await Promise.all([
        getProducts(),
        getSiteSettings(),
      ]);

      let finalCatalog = Array.isArray(fetchedSanityProducts) && fetchedSanityProducts.length > 0
        ? fetchedSanityProducts
        : localProducts;

      // 2. Fetch live Square Catalog if endpoint is available (Cloudflare Pages function)
      try {
        const sqRes = await fetch('/api/square-catalog');
        const sqData = await sqRes.json();

        if (sqData.configured && Array.isArray(sqData.items) && sqData.items.length > 0) {
          // Merge Square items with Sanity products:
          // Matches by squareCatalogId, squareVariationId, or name
          const merged = sqData.items.map(sqItem => {
            const matchInSanity = finalCatalog.find(p => 
              (p.squareCatalogId && p.squareCatalogId === sqItem.squareCatalogId) ||
              (p.squareVariationId && p.squareVariationId === sqItem.squareVariationId) ||
              (p.name && p.name.trim().toLowerCase() === sqItem.name.trim().toLowerCase())
            );

            if (matchInSanity) {
              return {
                ...matchInSanity,
                squareCatalogId: sqItem.squareCatalogId,
                squareVariationId: sqItem.squareVariationId,
                price: sqItem.price || matchInSanity.price,
                priceNum: sqItem.priceNum || matchInSanity.priceNum,
                name: sqItem.name || matchInSanity.name,
                variations: sqItem.variations || [],
                isLinkedToSquare: true,
              };
            }

            // Square item not yet enriched in Sanity: still display on website
            return {
              id: sqItem.id,
              name: sqItem.name,
              price: sqItem.price,
              priceNum: sqItem.priceNum,
              squareCatalogId: sqItem.squareCatalogId,
              squareVariationId: sqItem.squareVariationId,
              description: sqItem.description || '',
              image: 'https://images.unsplash.com/photo-1572375992501-4b0892d50c69?q=80&w=600&auto=format&fit=crop',
              itemType: 'ready-to-ship',
              inStock: true,
              tags: ['Square Item'],
              isFromSquare: true,
            };
          });

          // Include Sanity products that weren't in Square (e.g. custom or draft items)
          const matchedSquareNames = new Set(merged.map(m => m.name.trim().toLowerCase()));
          const remainingSanity = finalCatalog.filter(p => !matchedSquareNames.has(p.name.trim().toLowerCase()));

          finalCatalog = [...merged, ...remainingSanity];
        }
      } catch (sqErr) {
        // Fallback silently if offline or running local dev without functions server
      }

      setProducts(finalCatalog);
      updateSearchIndex(finalCatalog);

      if (fetchedSettings) {
        setSiteSettings(prev => ({ ...prev, ...fetchedSettings }));
      }
    } catch (err) {
      console.warn('Error loading dynamic product catalog:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  return (
    <ProductContext.Provider value={{ products, siteSettings, loading, refreshCatalog: fetchCatalog }}>
      {children}
    </ProductContext.Provider>
  );
};
